import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { FC } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TradeMatchingRow,
  useTradeMatchingRows,
} from "../dataHooks/tradeMatchingQueries";
import { CustomToolbar } from "../dataGridToolbar";

const columns: GridColDef<TradeMatchingRow>[] = [
  { field: "uti", headerName: "UTI", flex: 1.2, minWidth: 340 },
  { field: "isin", headerName: "ISIN", flex: 1, minWidth: 140 },
  {
    field: "datetimeTrade",
    headerName: "Datetime trade",
    width: 180,
    valueFormatter: (value: string | null) =>
      value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "",
  },
  {
    field: "tradeMatch",
    headerName: "Match",
    width: 100,
    renderCell: (params) =>
      params.value === 1 ? (
        <Chip label="Match" size="small" color="success" />
      ) : (
        <Chip label="No match" size="small" color="error" variant="outlined" />
      ),
  },
  { field: "currency", headerName: "Currency", width: 100 },
  {
    field: "mQuantity",
    headerName: "Quantity",
    width: 80,
    type: "number",
  },
  {
    field: "mQuantityEmirRaw",
    headerName: "Quantity (EMIR raw)",
    width: 160,
    type: "number",
  },
  {
    field: "mPriceLcy",
    headerName: "Price (LCY)",
    type: "number",
  },
  {
    field: "mNotionalAmountLcy",
    headerName: "Notional amount (LCY)",
    width: 180,
  },
  { field: "tradeDirection", headerName: "Direction", width: 110 },
];

type SourceType = "DTCC" | "REGIS";

const splitSourcePrefix = (
  source: string | null,
): { type: SourceType | null; displayName: string | null } => {
  if (!source) {
    return { type: null, displayName: null };
  }
  if (source.startsWith("DTCC_")) {
    return { type: "DTCC", displayName: source.slice("DTCC_".length) };
  }
  if (source.startsWith("Regis_")) {
    return { type: "REGIS", displayName: source.slice("Regis_".length) };
  }
  return { type: null, displayName: source };
};

export const FileDetails: FC = () => {
  const { source } = useParams<{ source: string }>();
  const navigate = useNavigate();
  const decodedSource = source ? decodeURIComponent(source) : null;

  const { data, isLoading, isFetching, error } =
    useTradeMatchingRows(decodedSource);

  const matchedCount = data?.filter((r) => r.tradeMatch === 1).length ?? 0;
  const { type: sourceType, displayName } = splitSourcePrefix(decodedSource);

  return (
    <Box
      sx={{
        padding: 4,
        maxWidth: 1600,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px - 56px)",
      }}
    >
      <Box sx={{ marginBottom: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ marginBottom: 2 }}
        >
          Back to files
        </Button>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            marginBottom: 1,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            color="text.primary"
            sx={{ fontWeight: 600 }}
          >
            {displayName ?? "—"}
          </Typography>
          {sourceType && (
            <Chip
              label={sourceType}
              size="small"
              color={sourceType === "DTCC" ? "info" : "warning"}
            />
          )}
        </Box>
        {data && (
          <Typography variant="body1" color="text.secondary">
            {matchedCount} / {data.length} matched
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {error.message}
        </Alert>
      )}

      {isLoading && !data && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && (
        <Box sx={{ flex: 1, minHeight: 400 }}>
          <DataGridPremium
            rows={data}
            columns={columns}
            getRowId={(row) => row.uti}
            loading={isFetching && !isLoading}
            density="compact"
            showToolbar
            initialState={{
              sorting: {
                sortModel: [
                  { field: "tradeMatch", sort: "asc" },
                  { field: "datetimeTrade", sort: "desc" },
                ],
              },
            }}
            slots={{ toolbar: CustomToolbar }}
          />
        </Box>
      )}
    </Box>
  );
};
